import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import { ModalWindow, Select, Text, useLocalizeMessage } from 'libs.nucleus';
import { channelDisplayName, planTemplateDelivery, } from '../notification_channel_delivery.helpers';
import { useNotificationTemplates } from '../use_notification_templates.hook';
import { isNotificationSendOffset, NOTIFICATION_SEND_OFFSETS, useSendNotification, } from '../use_send_notification.hook';
const TIMING_LABEL_KEYS = {
    0: 'label.timeline.timing_now',
    5: 'label.timeline.timing_5',
    15: 'label.timeline.timing_15',
    30: 'label.timeline.timing_30',
    60: 'label.timeline.timing_60',
};
const channelSummary = (template) => {
    if (!template?.channels?.length) {
        return '';
    }
    return template.channels
        .map((channel) => channelDisplayName(channel))
        .filter(Boolean)
        .join(', ');
};
/**
 * Modal for sending a study notification template to the participant on the timeline.
 * Loads templates from GET c_sites/:siteId/c_notification_templates and sends via
 * POST c_sites/:siteId/c_public_users/:publicUserId/c_notifications (AXONCONFIG-5254).
 *
 * Send is enabled when at least one template channel is deliverable given the
 * participant's email / mobile / account. The backend only attempts those channels.
 */
export const SendNotificationModal = ({ isOpen, onClose, participantId, siteId, hasParticipantEmail, hasParticipantMobile, hasParticipantAccount, isParticipantDetailsReady = true, isParticipantDetailsError = false, initialOffset = 0, initialNotificationName, }) => {
    const translate = useLocalizeMessage();
    const { send, isSending } = useSendNotification();
    const { templates, isLoading: isLoadingTemplates, isError: isTemplatesError, } = useNotificationTemplates(siteId, {
        enabled: isOpen && !!siteId,
    });
    const [offset, setOffset] = useState(initialOffset);
    const [selectedNotificationName, setSelectedNotificationName] = useState(initialNotificationName);
    useEffect(() => {
        if (isOpen) {
            setOffset(initialOffset);
            setSelectedNotificationName(initialNotificationName);
        }
    }, [isOpen, initialOffset, initialNotificationName]);
    const capabilities = useMemo(() => ({
        hasEmail: hasParticipantEmail,
        hasMobile: hasParticipantMobile,
        hasAccount: hasParticipantAccount,
    }), [hasParticipantEmail, hasParticipantMobile, hasParticipantAccount]);
    const timingOptions = NOTIFICATION_SEND_OFFSETS.map((value) => ({
        value: String(value),
        label: translate(TIMING_LABEL_KEYS[value]),
    }));
    const selectedTiming = timingOptions.find((option) => option.value === String(offset)) ?? timingOptions[0];
    const placeholderLabel = isLoadingTemplates
        ? 'label.timeline.template_loading'
        : isTemplatesError
            ? 'label.timeline.template_unavailable'
            : templates.length === 0
                ? 'label.timeline.template_empty'
                : 'label.timeline.template_placeholder';
    const placeholderOption = {
        value: '',
        label: translate(placeholderLabel),
    };
    const sortedTemplateOptions = templates
        .map((template) => ({
        value: template.name,
        label: template.label || template.name,
    }))
        .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
    // Drop the placeholder once a template is selected so picking it can't no-op.
    const templateOptions = templates.length === 0
        ? [placeholderOption]
        : selectedNotificationName
            ? sortedTemplateOptions
            : [placeholderOption, ...sortedTemplateOptions];
    const selectedTemplate = sortedTemplateOptions.find((option) => option.value === selectedNotificationName) ?? placeholderOption;
    const selectedTemplateData = templates.find((template) => template.name === selectedNotificationName);
    const channels = channelSummary(selectedTemplateData);
    const deliveryPlan = planTemplateDelivery(selectedTemplateData, capabilities);
    const blockedForNoChannels = !!selectedNotificationName && hasParticipantAccount && !deliveryPlan.canSend;
    const onSubmit = async () => {
        if (!selectedNotificationName || !deliveryPlan.canSend || isSending) {
            return;
        }
        try {
            await send({
                participantId,
                siteId,
                notificationName: selectedNotificationName,
                offsetMinutes: offset,
            });
            // Keep the dialog open so site users can send/test another template.
        }
        catch {
            // Error toast is handled in useSendNotification.
        }
    };
    const onTimingChange = (option) => {
        const next = Number(option.value);
        if (isNotificationSendOffset(next)) {
            setOffset(next);
        }
    };
    const onTemplateChange = (option) => {
        if (option.value) {
            setSelectedNotificationName(option.value);
        }
    };
    const canSubmit = !!selectedNotificationName &&
        !isSending &&
        !isLoadingTemplates &&
        deliveryPlan.canSend &&
        isParticipantDetailsReady &&
        !isParticipantDetailsError;
    return (_jsx(ModalWindow, { isOpen: isOpen, closeWindow: onClose, hideCloseIcon: false, width: 'sm', dataTestId: 'send-notification-modal', title: translate('label.timeline.send_notification_title'), subtitle: translate('label.timeline.send_notification_subtitle'), footerPrimaryActionButton: {
            label: offset === 0 ? translate('action.timeline.send_now') : translate('action.timeline.schedule'),
            onClick: onSubmit,
            disabled: !canSubmit,
            dataTestId: 'send-notification-submit',
        }, footerSecondaryActionButtons: [
            { label: translate('generic.cancel'), onClick: onClose, dataTestId: 'send-notification-cancel' },
        ], children: _jsxs("div", { className: 'flex flex-col gap-4', children: [isParticipantDetailsError && (_jsx("div", { "data-testid": 'send-notification-details-unavailable', children: _jsx(Text, { size: 'sm', className: 'text-text-secondary', children: translate('info.timeline.participant_details_unavailable') }) })), isParticipantDetailsReady && !hasParticipantAccount && (_jsx("div", { "data-testid": 'send-notification-no-account-info', children: _jsx(Text, { size: 'sm', className: 'text-text-secondary', children: translate('info.timeline.participant_no_account') }) })), _jsx(Select, { dataTestId: 'send-notification-template', label: translate('label.timeline.template'), options: templateOptions, value: selectedTemplate, disabled: isLoadingTemplates || isTemplatesError || templates.length === 0, onChange: onTemplateChange }), selectedNotificationName && (_jsx("div", { "data-testid": 'send-notification-channels', children: _jsxs(Text, { size: 'sm', className: 'text-text-secondary', children: [translate('label.timeline.channels'), ": ", channels || translate('label.timeline.notif_none')] }) })), blockedForNoChannels && (_jsx("div", { "data-testid": 'send-notification-channels-blocked', children: _jsx(Text, { size: 'sm', className: 'text-text-secondary', children: translate('info.timeline.no_deliverable_channels') }) })), deliveryPlan.isPartial && (_jsx("div", { "data-testid": 'send-notification-partial-channels', children: _jsx(Text, { size: 'sm', className: 'text-text-secondary', children: translate('info.timeline.partial_channels', {
                            willSend: deliveryPlan.deliverable.map(channelDisplayName).filter(Boolean).join(', '),
                            skipped: deliveryPlan.skipped.map(channelDisplayName).filter(Boolean).join(', '),
                        }) }) })), _jsx(Select, { dataTestId: 'send-notification-timing', label: translate('label.timeline.send_label'), options: timingOptions, value: selectedTiming, onChange: onTimingChange })] }) }));
};
